import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { useStore } from '../store';
import styles from './AuthPage.module.css';

type Mode = 'login' | 'register' | 'email-sent' | 'set-password';

export function AuthPage() {
  const login = useStore((s) => s.login);
  const logout = useStore((s) => s.logout);
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteInfo, setInviteInfo] = useState<{ email: string; role: string; workspaceName?: string } | null>(null);

  // Check URL for ?verify=token or ?invite=token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle registration verification
    const vToken = params.get('verify');
    if (vToken) {
      fetch(`/auth/verify-check?token=${vToken}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.valid) {
            setVerifyToken(vToken);
            setVerifyEmail(data.email);
            setMode('set-password');
          } else {
            setError('This verification link is invalid or expired.');
          }
          window.history.replaceState({}, '', '/web/');
        })
        .catch(() => {
          setError('Failed to verify link.');
          window.history.replaceState({}, '', '/web/');
        });
      return;
    }

    // Handle workspace invite — log out current user first so the new account can be set up
    const iToken = params.get('invite');
    if (iToken) {
      logout();
      fetch(`/auth/invite-check?token=${iToken}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.valid) {
            setInviteToken(iToken);
            setInviteInfo({ email: data.email, role: data.role, workspaceName: data.workspaceName });
            setMode('set-password');
          } else {
            setError(data.reason === 'expired' ? 'This invitation has expired.' : 'This invitation link is invalid.');
          }
          window.history.replaceState({}, '', '/web/');
        })
        .catch(() => {
          setError('Failed to verify invitation.');
          window.history.replaceState({}, '', '/web/');
        });
    }
  }, []);

  // Handle register: send verification email
  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || `Failed (${res.status})`); return; }
      setMode('email-sent');
    } catch {
      setError('Network error. Is the server running?');
    } finally {
      setLoading(false);
    }
  }

  // Handle set password: complete registration or accept invite
  async function handleSetPassword(e: FormEvent) {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter a password.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Invite flow vs registration flow
      const isInvite = !!inviteToken;
      const endpoint = isInvite ? '/auth/invite/accept' : '/auth/verify';
      const body = isInvite
        ? { token: inviteToken, password }
        : { token: verifyToken, password };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || `Failed (${res.status})`); return; }
      // Set onboarding flag for self-registration (not invite)
      if (!isInvite) {
        localStorage.setItem('novax:needsOnboarding', 'true');
      }
      login(data.token, {
        id: data.user.id,
        machineApiKey: data.user.machineApiKey,
        name: data.user.name,
        email: data.user.email,
        avatarUrl: data.user.avatarUrl,
      });
    } catch {
      setError('Network error. Is the server running?');
    } finally {
      setLoading(false);
    }
  }

  // Handle login
  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || `Failed (${res.status})`); return; }
      login(data.token, {
        id: data.user.id,
        machineApiKey: data.user.machineApiKey,
        name: data.user.name,
        email: data.user.email,
        avatarUrl: data.user.avatarUrl,
      });
    } catch {
      setError('Network error. Is the server running?');
    } finally {
      setLoading(false);
    }
  }

  function renderForm() {
    // Email sent confirmation
    if (mode === 'email-sent') {
      return (
        <>
          <span className={styles.eyebrow}>CHECK YOUR INBOX</span>
          <h1 className={styles.title}>Email sent<span className={styles.titleDot}>.</span></h1>
          <p className={styles.subtitle}>
            We sent a verification link to <strong>{email}</strong>. Click the link to set your password and complete registration.
          </p>
          <p className={styles.subtitle} style={{ fontSize: '12px', color: '#999' }}>
            Didn't receive it? Check your spam folder, or{' '}
            <button type="button" className={styles.switchLink} onClick={() => { setMode('register'); setError(''); }}>
              try again
            </button>
          </p>
          <div className={styles.switchRow}>
            <span>Already have an account?</span>{' '}
            <button type="button" className={styles.switchLink} onClick={() => { setMode('login'); setError(''); }}>
              Sign in →
            </button>
          </div>
        </>
      );
    }

    // Set password (from verification link or invite link)
    if (mode === 'set-password') {
      const isInvite = !!inviteToken;
      const displayEmail = isInvite ? inviteInfo?.email : verifyEmail;
      return (
        <form onSubmit={handleSetPassword}>
          <span className={styles.eyebrow}>{isInvite ? 'ACCEPT INVITATION' : 'COMPLETE REGISTRATION'}</span>
          <h1 className={styles.title}>{isInvite ? 'Join organization' : 'Set your password'}<span className={styles.titleDot}>.</span></h1>
          <p className={styles.subtitle}>
            {isInvite ? (
              <>You've been invited to join <strong>{inviteInfo?.workspaceName || 'an organization'}</strong>. Set your password to get started.</>
            ) : (
              <>Setting up account for <strong>{displayEmail}</strong></>
            )}
          </p>

          <div className={styles.fields}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <label className={styles.label}>PASSWORD</label>
              </div>
              <div className={styles.inputWrap}>
                <svg className={styles.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input className={styles.input} type={showPw ? 'text' : 'password'} placeholder="At least 8 characters" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} autoFocus autoComplete="new-password" />
                <button type="button" className={styles.showBtn} onClick={() => setShowPw(!showPw)}>
                  {showPw ? 'HIDE' : 'SHOW'}
                </button>
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <label className={styles.label}>CONFIRM PASSWORD</label>
              </div>
              <div className={styles.inputWrap}>
                <svg className={styles.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input className={styles.input} type={showPw ? 'text' : 'password'} placeholder="Re-enter your password" value={confirmPw} onChange={(e) => { setConfirmPw(e.target.value); setError(''); }} autoComplete="new-password" />
              </div>
            </div>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button className={styles.submitBtn} type="submit" disabled={loading}>
            <span>{loading ? 'Creating account...' : 'Create account'}</span>
            {!loading && <span className={styles.submitArrow}>→</span>}
          </button>
        </form>
      );
    }

    // Register: email only
    if (mode === 'register') {
      return (
        <form onSubmit={handleRegister}>
          <span className={styles.eyebrow}>CREATE YOUR ACCOUNT</span>
          <h1 className={styles.title}>Get started<span className={styles.titleDot}>.</span></h1>
          <p className={styles.subtitle}>
            Enter your email and we'll send you a verification link.
          </p>

          <div className={styles.fields}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <label className={styles.label}>EMAIL</label>
              </div>
              <div className={styles.inputWrap}>
                <svg className={styles.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <input className={styles.input} type="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} autoFocus autoComplete="email" />
              </div>
            </div>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button className={styles.submitBtn} type="submit" disabled={loading}>
            <span>{loading ? 'Sending...' : 'Send verification email'}</span>
            {!loading && <span className={styles.submitArrow}>→</span>}
          </button>

          <div className={styles.switchRow}>
            <span>Already have an account?</span>{' '}
            <button type="button" className={styles.switchLink} onClick={() => { setMode('login'); setError(''); }}>
              Sign in →
            </button>
          </div>
        </form>
      );
    }

    // Login
    return (
      <form onSubmit={handleLogin}>
        <span className={styles.eyebrow}>SIGN IN TO NOVAX</span>
        <h1 className={styles.title}>Welcome back<span className={styles.titleDot}>.</span></h1>
        <p className={styles.subtitle}>
          Enter your email and password to enter your organization.
        </p>

        <div className={styles.fields}>
          <div className={styles.field}>
            <div className={styles.labelRow}>
              <label className={styles.label}>EMAIL</label>
            </div>
            <div className={styles.inputWrap}>
              <svg className={styles.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <input className={styles.input} type="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} autoFocus autoComplete="email" />
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.labelRow}>
              <label className={styles.label}>PASSWORD</label>
            </div>
            <div className={styles.inputWrap}>
              <svg className={styles.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input className={styles.input} type={showPw ? 'text' : 'password'} placeholder="••••••••••" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} autoComplete="current-password" />
              <button type="button" className={styles.showBtn} onClick={() => setShowPw(!showPw)}>
                {showPw ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </div>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <button className={styles.submitBtn} type="submit" disabled={loading}>
          <span>{loading ? 'Signing in...' : 'Sign in'}</span>
          {!loading && <span className={styles.submitArrow}>→</span>}
        </button>

        <div className={styles.switchRow}>
          <span>No account?</span>{' '}
          <button type="button" className={styles.switchLink} onClick={() => { setMode('register'); setError(''); }}>
            Request access →
          </button>
        </div>
      </form>
    );
  }

  // ─── Canvas light trail on poster ───
  const posterRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trailPoints = useRef<{ x: number; y: number; age: number; vx: number; vy: number }[]>([]);
  const mouseActive = useRef(false);
  const animRef = useRef<number>(0);

  // Animation loop: draw fading trails
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let running = true;

    function resize() {
      const rect = posterRef.current?.getBoundingClientRect();
      if (!rect || !canvas) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.scale(dpr, dpr);
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      if (!running || !canvas) return;
      const rect = posterRef.current?.getBoundingClientRect();
      if (!rect) { animRef.current = requestAnimationFrame(draw); return; }
      const w = rect.width, h = rect.height;

      ctx.clearRect(0, 0, w, h);

      const points = trailPoints.current;
      // Age all points
      for (let i = points.length - 1; i >= 0; i--) {
        points[i].age += 1;
        points[i].x += points[i].vx;
        points[i].y += points[i].vy;
        points[i].vx *= 0.97;
        points[i].vy *= 0.97;
        if (points[i].age > 90) { points.splice(i, 1); }
      }

      // Draw connected flowing trail
      if (points.length > 1) {
        for (let i = 0; i < points.length; i++) {
          const p = points[i];
          const life = 1 - p.age / 90;
          const r = 20 + life * 50;

          // Soft glow — lighter center
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
          grad.addColorStop(0, `rgba(244, 164, 138, ${0.15 * life})`);
          grad.addColorStop(0.4, `rgba(227, 74, 42, ${0.08 * life})`);
          grad.addColorStop(0.7, `rgba(90, 106, 42, ${0.03 * life})`);
          grad.addColorStop(1, 'transparent');
          ctx.fillStyle = grad;
          ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
        }

        // Draw a bright connecting line through recent points
        const recent = points.filter(p => p.age < 25);
        if (recent.length > 2) {
          ctx.beginPath();
          ctx.moveTo(recent[0].x, recent[0].y);
          for (let i = 1; i < recent.length; i++) {
            const prev = recent[i - 1], curr = recent[i];
            const mx = (prev.x + curr.x) / 2, my = (prev.y + curr.y) / 2;
            ctx.quadraticCurveTo(prev.x, prev.y, mx, my);
          }
          ctx.strokeStyle = 'rgba(244, 164, 138, 0.2)';
          ctx.lineWidth = 1.5;
          ctx.shadowColor = 'rgba(227, 74, 42, 0.3)';
          ctx.shadowBlur = 12;
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
      }

      animRef.current = requestAnimationFrame(draw);
    }
    animRef.current = requestAnimationFrame(draw);

    return () => { running = false; cancelAnimationFrame(animRef.current); window.removeEventListener('resize', resize); };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = posterRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mouseActive.current = true;
    // Add trail point with slight random spread
    const spread = 3;
    trailPoints.current.push({
      x: x + (Math.random() - 0.5) * spread,
      y: y + (Math.random() - 0.5) * spread,
      age: 0,
      vx: (Math.random() - 0.5) * 1.5,
      vy: (Math.random() - 0.5) * 1.5,
    });
    // Keep max 120 points
    if (trailPoints.current.length > 120) trailPoints.current.splice(0, trailPoints.current.length - 120);
  }, []);

  const handleMouseLeave = useCallback(() => {
    mouseActive.current = false;
  }, []);

  return (
    <div className={styles.shell}>
      {/* Left: dark typographic poster */}
      <div className={styles.poster} ref={posterRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        {/* Aurora flowing background */}
        <div className={styles.aurora} />
        <div className={styles.auroraInner} />
        {/* Canvas light trail */}
        <canvas ref={canvasRef} className={styles.trailCanvas} />
        {/* Floating particles */}
        <div className={styles.particles}>
          {Array.from({ length: 24 }, (_, i) => (
            <span key={i} className={styles.particle} style={{ '--i': i } as React.CSSProperties} />
          ))}
        </div>
        {/* Grid lines */}
        <div className={styles.gridOverlay} />
        <div className={styles.redShape} />
        <div className={styles.topBar}>
          <div className={styles.logoBox}>
            <span className={styles.logoN}>N</span>
            <span className={styles.logoX}>X</span>
          </div>
          <div className={styles.logoText}>
            <span className={styles.logoName}>NovaX</span>
            <span className={styles.logoVersion}>ORGANIZATION · V0.4</span>
          </div>
        </div>
        <div className={styles.quoteArea}>
          <div className={styles.quote}>
            agents,<br />
            but<br />
            <span className={styles.quoteAccent}>coworkers</span>
            <span className={styles.quoteDot}>.</span>
          </div>
        </div>
        <div className={styles.posterBottom}>
          <div className={styles.manifesto}>
            — NOVAX MANIFESTO · 2026 · § 01<br />
            an organization where your agent team has a desk, a chat,<br />
            and a place to put their things down.
          </div>
          <div className={styles.footerLinks}>
            © NOVAX · 2026 &nbsp;·&nbsp; STATUS &nbsp;·&nbsp; PRIVACY &nbsp;·&nbsp; TERMS
          </div>
        </div>
      </div>

      {/* Right: auth form */}
      <div className={styles.formSide}>
        <div className={styles.form}>
          {renderForm()}
        </div>
      </div>
    </div>
  );
}
